import { api } from './api';

export type ConnectorKind =
  | 'googledrive'
  | 'm365'
  | 'slack'
  | 'confluence'
  | 'notion'
  | 'salesforce'
  | 'box'
  | 'github'
  | 'jira'
  | 'zendesk'
  | 'dropbox'
  | 'kintone'
  | 'chatwork'
  | 'lineworks';

export interface ConnectorDescriptor {
  kind: ConnectorKind;
  label: string;
  region: 'global' | 'jp';
  description: string;
  partnerDocUrl: string;
  requiresSubdomain: boolean;
}

export interface ConnectorRow {
  id: string;
  kind: ConnectorKind;
  label: string;
  displayName: string;
  status: 'pending' | 'authorizing' | 'active' | 'syncing' | 'error' | 'disabled';
  lastSyncedAt: string | null;
  lastError: string | null;
  hasCredentials: boolean;
  createdAt: string;
}

export const connectorsApi = {
  async catalog(): Promise<ConnectorDescriptor[]> {
    const r = await api.get<ConnectorDescriptor[]>('/api/connectors/catalog');
    return r.data;
  },
  async list(wsId: string): Promise<ConnectorRow[]> {
    const r = await api.get<ConnectorRow[]>(`/api/connectors/workspaces/${wsId}`);
    return r.data;
  },
  async create(wsId: string, kind: ConnectorKind, displayName?: string): Promise<ConnectorRow> {
    const r = await api.post<ConnectorRow>(`/api/connectors/workspaces/${wsId}`, {
      kind,
      displayName,
    });
    return r.data;
  },
  startUrl(connectorId: string, subdomain?: string): string {
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '';
    const sub = subdomain ? `?subdomain=${encodeURIComponent(subdomain)}` : '';
    return `${base}/api/connectors/${connectorId}/oauth/start${sub}`;
  },
  /** Disconnect + remove a connector (wipes credentials, stops scheduled sync). */
  async remove(connectorId: string): Promise<{ ok: boolean }> {
    const r = await api.delete<{ ok: boolean }>(`/api/connectors/${connectorId}`);
    return r.data;
  },
  /** Trigger an immediate sync. Runs synchronously and returns item counts. */
  async sync(connectorId: string): Promise<unknown> {
    const r = await api.post(`/api/connectors/${connectorId}/sync`);
    return r.data;
  },
};
